import { Activity, Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/Button.js';
import { Card, CardContent } from '@/components/ui/Card.js';
import { Label } from '@/components/ui/Label.js';
import { useAuth } from '@/lib/auth.js';
import { ApiError, api, type PublicSsoProviders } from '@/api/client.js';

/**
 * Login page. Two modes:
 *
 *   1. Default (no `?sso=` query param): email + password form only.
 *
 *   2. Org-scoped SSO (`?sso=<orgSlug>`): the page fetches that org's enabled
 *      SSO providers and renders the appropriate "Continue with X for {Org}"
 *      buttons. Password fallback is still available. No platform-wide OAuth
 *      buttons appear here — SSO is always tied to a specific org.
 *
 * Error surfacing: callbacks from /api/v1/auth/sso/.../callback redirect back
 * to the page they came from with `?sso_error=` + `?sso_error_message=`. We
 * read those query params and show the message.
 */
/** Must match the backend's MIN_PASSWORD_LENGTH in packages/api/src/routes/auth.ts.
 *  Surface it inline as a hint so the user doesn't get an opaque 422. */
const MIN_PASSWORD_LENGTH = 12;

export function LoginPage(): JSX.Element {
  const { login, completePasswordChange, completeTwoFactor, status } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ssoState, setSsoState] = useState<
    { kind: 'none' } | { kind: 'loading' } | { kind: 'loaded'; data: PublicSsoProviders } | { kind: 'error'; message: string }
  >({ kind: 'none' });

  /**
   * /auth/login can hand us one of three stages. The challenge tokens are
   * held in component state only — never written to localStorage. Refreshing
   * the page forces the user to log in again, which is the right safety
   * posture for any in-flight challenge.
   */
  const [stage, setStage] = useState<
    | { kind: 'login' }
    | { kind: 'set-new-password'; challengeToken: string; user: { email: string; name: string } }
    | { kind: 'two-factor'; challengeToken: string; user: { email: string; name: string } }
  >({ kind: 'login' });
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  useEffect(() => {
    if (status === 'authenticated') void navigate({ to: '/' });
  }, [status, navigate]);

  // Read `sso_error*` params + load `?sso=<slug>` if present.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errCode = params.get('sso_error');
    const errMsg = params.get('sso_error_message');
    if (errCode) {
      setError(humaniseSsoError(errCode, errMsg));
      const url = new URL(window.location.href);
      url.searchParams.delete('sso_error');
      url.searchParams.delete('sso_error_message');
      window.history.replaceState(null, '', url.toString());
    }

    const slug = params.get('sso');
    if (slug) {
      setSsoState({ kind: 'loading' });
      api.sso
        .providersFor(slug)
        .then((data) => setSsoState({ kind: 'loaded', data }))
        .catch((err) =>
          setSsoState({
            kind: 'error',
            message:
              err instanceof ApiError && err.status === 404
                ? `No organisation matches "${slug}".`
                : err instanceof ApiError
                  ? err.message
                  : 'Could not load SSO providers.',
          }),
        );
    }
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login({ email, password });
      if (result.status === 'password-change-required') {
        // Transition into the new-password stage. Don't navigate — the user
        // isn't fully authenticated yet (no session cookie was issued).
        setStage({
          kind: 'set-new-password',
          challengeToken: result.challengeToken,
          user: result.user,
        });
        setPassword('');
      } else if (result.status === 'two-factor-required') {
        setStage({
          kind: 'two-factor',
          challengeToken: result.challengeToken,
          user: result.user,
        });
        setPassword('');
      } else {
        await navigate({ to: '/' });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitNewPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (stage.kind !== 'set-new-password') return;
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError(`Passwords don't match.`);
      return;
    }
    setSubmitting(true);
    try {
      await completePasswordChange({
        challengeToken: stage.challengeToken,
        newPassword,
      });
      await navigate({ to: '/' });
    } catch (err) {
      // 401 means the challenge expired — make that legible rather than
      // dumping the raw API message which says "invalid or expired".
      if (err instanceof ApiError && err.status === 401) {
        setError('Your password-change session expired. Sign in again to restart.');
        setStage({ kind: 'login' });
        setNewPassword('');
        setNewPasswordConfirm('');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not set the new password. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitTwoFactor(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (stage.kind !== 'two-factor') return;
    if (!twoFactorCode.trim()) {
      setError('Enter a 6-digit code, or a recovery code.');
      return;
    }
    setSubmitting(true);
    try {
      await completeTwoFactor({
        challengeToken: stage.challengeToken,
        code: twoFactorCode.trim(),
      });
      await navigate({ to: '/' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(err.message || 'That code did not verify. Try again.');
        setTwoFactorCode('');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not complete sign-in. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-6 py-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Activity className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-xl font-semibold">DeployIt</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {stage.kind === 'set-new-password'
                  ? `Set a new password for ${stage.user.email}`
                  : stage.kind === 'two-factor'
                    ? `Two-factor code for ${stage.user.email}`
                    : ssoState.kind === 'loaded'
                      ? `Sign in to ${ssoState.data.organisation.name}`
                      : 'Sign in to continue'}
              </p>
            </div>
          </div>

          {stage.kind === 'set-new-password' && (
            <PasswordChangePanel
              email={stage.user.email}
              newPassword={newPassword}
              setNewPassword={setNewPassword}
              newPasswordConfirm={newPasswordConfirm}
              setNewPasswordConfirm={setNewPasswordConfirm}
              error={error}
              submitting={submitting}
              onSubmit={onSubmitNewPassword}
              onCancel={() => {
                setStage({ kind: 'login' });
                setNewPassword('');
                setNewPasswordConfirm('');
                setError(null);
              }}
            />
          )}

          {stage.kind === 'two-factor' && (
            <TwoFactorChallengePanel
              code={twoFactorCode}
              setCode={setTwoFactorCode}
              error={error}
              submitting={submitting}
              onSubmit={onSubmitTwoFactor}
              onCancel={() => {
                setStage({ kind: 'login' });
                setTwoFactorCode('');
                setError(null);
              }}
            />
          )}

          {stage.kind === 'login' && (
            <>
          {/* original login content begins */}

          {ssoState.kind === 'loading' && (
            <div className="flex justify-center py-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}

          {ssoState.kind === 'error' && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
              {ssoState.message}
            </div>
          )}

          {ssoState.kind === 'loaded' && ssoState.data.providers.length > 0 && (
            <>
              <div className="space-y-2">
                {ssoState.data.providers.map((p) => (
                  <Button key={p.provider} asChild variant="outline" className="w-full justify-center">
                    <a href={p.startUrl}>
                      <ProviderIcon provider={p.provider} />
                      Continue with {labelFor(p.provider)} for {ssoState.data.organisation.name}
                    </a>
                  </Button>
                ))}
              </div>
              <Divider label="or with email" />
            </>
          )}

          {ssoState.kind === 'loaded' && ssoState.data.providers.length === 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
              {ssoState.data.organisation.name} has no SSO providers configured. Sign in with email below.
            </div>
          )}

          <form className="space-y-3" onSubmit={onSubmit}>
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <input
              type="password"
              required
              autoComplete="current-password"
              minLength={12}
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {error && (
              <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            <Button className="w-full" type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>

          <div className="text-center text-xs text-muted-foreground">
            New users join by invitation only. Ask an admin to invite you.
          </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Set-new-password panel shown when /auth/login returns
 * password-change-required. The challenge token lives in the parent component
 * only (no localStorage) and the panel just collects the new password.
 */
function PasswordChangePanel({
  email,
  newPassword,
  setNewPassword,
  newPasswordConfirm,
  setNewPasswordConfirm,
  error,
  submitting,
  onSubmit,
  onCancel,
}: {
  email: string;
  newPassword: string;
  setNewPassword: (v: string) => void;
  newPasswordConfirm: string;
  setNewPasswordConfirm: (v: string) => void;
  error: string | null;
  submitting: boolean;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
}): JSX.Element {
  function generate() {
    // 18 random bytes → ~24 base64url chars, easily exceeds the 12-char min.
    // Done in-browser via crypto.getRandomValues; the value never leaves the
    // SPA until the user submits the form.
    const bytes = new Uint8Array(18);
    window.crypto.getRandomValues(bytes);
    const out = btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    setNewPassword(out);
    setNewPasswordConfirm(out);
  }

  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      {/* Amber colour was previously `text-amber-200` only, which is designed
          for dark backgrounds. On the Login Card (light surface) it rendered
          near-invisible. Pair a dark-amber for light mode with the light shade
          for dark mode so the warning is legible everywhere. */}
      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
        Your account uses a one-time password set by an admin. Set a new
        password for <span className="font-medium">{email}</span> to continue.
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="new-password">New password</Label>
        <input
          id="new-password"
          type="password"
          required
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="confirm-password">Confirm new password</Label>
        <input
          id="confirm-password"
          type="password"
          required
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          value={newPasswordConfirm}
          onChange={(e) => setNewPasswordConfirm(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Minimum {MIN_PASSWORD_LENGTH} characters. A passphrase ({MIN_PASSWORD_LENGTH}+ chars across
        a few words) is easier to remember and harder to guess than a short
        password with symbols.
      </p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={generate}>
          Generate a strong one
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive dark:text-destructive-foreground">
          {error}
        </p>
      )}
      <Button className="w-full" type="submit" disabled={submitting}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set new password'}
      </Button>
    </form>
  );
}

/**
 * Two-factor challenge panel — shown when /auth/login returns
 * two-factor-required. Accepts either a 6-digit TOTP code or one of the
 * printable recovery codes (XXXX-XXXX-XXXX). The input type is plain text
 * with autoComplete="one-time-code" so password managers offer to autofill,
 * and inputMode="numeric" tips mobile browsers to show the digit keyboard.
 */
function TwoFactorChallengePanel({
  code,
  setCode,
  error,
  submitting,
  onSubmit,
  onCancel,
}: {
  code: string;
  setCode: (v: string) => void;
  error: string | null;
  submitting: boolean;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        Enter the 6-digit code from your authenticator app, or one of your printable recovery codes
        (XXXX-XXXX-XXXX) if you've lost access to it.
      </div>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        required
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="123456"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-center font-mono text-lg tracking-widest placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive dark:text-destructive-foreground">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || code.trim().length === 0} className="flex-1">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
        </Button>
      </div>
    </form>
  );
}

function Divider({ label }: { label: string }): JSX.Element {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-border" />
      </div>
      <div className="relative flex justify-center text-xs uppercase tracking-wider">
        <span className="bg-card px-2 text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

function labelFor(provider: 'google' | 'github'): string {
  return provider === 'google' ? 'Google' : 'GitHub';
}

function ProviderIcon({ provider }: { provider: 'google' | 'github' }): JSX.Element {
  if (provider === 'google') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18a10.997 10.997 0 0 0 0 9.86l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
      <path d="M12 .5a11.5 11.5 0 0 0-3.63 22.41c.58.1.79-.25.79-.55v-2c-3.2.7-3.88-1.36-3.88-1.36-.52-1.32-1.27-1.67-1.27-1.67-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.74 2.68 1.24 3.34.95.1-.74.4-1.24.73-1.52-2.55-.29-5.24-1.27-5.24-5.65 0-1.25.45-2.27 1.18-3.06-.12-.29-.51-1.45.11-3.03 0 0 .96-.31 3.15 1.17a10.96 10.96 0 0 1 5.74 0c2.19-1.48 3.15-1.17 3.15-1.17.63 1.58.24 2.74.12 3.03.73.79 1.18 1.81 1.18 3.06 0 4.39-2.7 5.36-5.27 5.64.41.36.78 1.06.78 2.14v3.17c0 .31.21.66.8.55A11.5 11.5 0 0 0 12 .5z" />
    </svg>
  );
}

function humaniseSsoError(code: string, raw: string | null): string {
  switch (code) {
    case 'sso_no_account':
      return raw ?? 'The provider authenticated you, but you have no DeployIt account and auto-provisioning is off. Ask an admin for an invitation.';
    case 'sso_matcher_failed':
      return raw ?? 'You authenticated successfully, but you\'re not a member of the org\'s configured upstream tenant.';
    case 'sso_not_configured':
      return raw ?? 'SSO is not configured for that organisation.';
    case 'sso_org_unknown':
      return raw ?? 'That organisation does not exist.';
    case 'sso_denied':
      return 'You denied the authorisation request.';
    case 'sso_state_mismatch':
    case 'sso_callback_mismatch':
    case 'sso_bad_callback':
    case 'sso_missing_pkce':
      return 'The sign-in could not be completed safely. Try again.';
    case 'sso_code_exchange_failed':
      return raw ?? 'The provider could not complete the sign-in. Try again.';
    case 'sso_unknown_provider':
      return 'That sign-in provider is not supported.';
    default:
      return raw ?? `Sign-in failed (${code}).`;
  }
}
